import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProjectBySlug, updates } from '../data/unifiedEcosystem';
import { ExternalLink, Play, Award, Calendar, ChevronDown } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

// Collapsible News Component
const CollapsibleNews = ({ title, items }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <h3 className="text-xl editorial-serif">
          {title}
        </h3>
        <ChevronDown 
          size={20} 
          className={`text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {isOpen && (
        <div className="p-6 border-t border-border">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((item) => (
              <Link 
                key={item.id}
                to={`/${item.slug}`}
                className="block group"
                data-testid={`project-content-${item.slug}`}
              >
                <div className="relative aspect-[16/9] bg-black overflow-hidden mb-4">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest">
                    <span className="px-2 py-0.5 bg-primary/10 text-primary">{item.category}</span>
                    {item.source && (
                      <span>{item.source}</span>
                    )}
                  </div>
                  <h3 className="text-lg editorial-serif group-hover:accent-red transition-colors line-clamp-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {item.dek}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar size={12} />
                    <span>
                      {new Date(item.date).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Get content items related to a specific project
const getProjectContent = (projectTitle) => {
  return updates.filter(item => {
    if (item.project && item.project.toLowerCase() === projectTitle.toLowerCase()) {
      return true;
    }
    return false;
  });
};

export const ProjectDetailPage = () => {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);
  const projectContent = project ? getProjectContent(project.title) : [];

  if (!project) {
    return (
      <div className="editorial-container py-32 text-center">
        <p className="text-muted-foreground text-sm uppercase tracking-widest">Project not found</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{project.title} | Cultural Revolution</title>
        <meta name="description" content={project.synopsis || project.tagline} />
        <meta property="og:title" content={`${project.title} | Cultural Revolution`} />
        <meta property="og:description" content={project.synopsis || project.tagline} />
        <meta property="og:image" content={project.image} />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen" data-testid="project-detail-page">
        {/* Hero */}
        <section className="editorial-container py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            <div className="relative aspect-[4/3] bg-black" data-testid="project-hero-image">
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover opacity-90"
              />
            </div>

            <div className="flex flex-col justify-center space-y-8">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest accent-red">
                  {project.status}
                </span>
              </div>
              
              <h1 
                className="text-5xl sm:text-6xl lg:text-7xl editorial-serif leading-none"
                data-testid="project-title"
              >
                {project.title}
              </h1>
              
              <p className="text-lg text-foreground/80 leading-relaxed">
                {project.synopsis}
              </p>

              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground uppercase tracking-wider">
                <span>{project.type}</span>
                <span>·</span>
                <span>{project.year}</span>
                <span>·</span>
                <span>{project.company}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Streaming Platforms */}
        {project.platforms && project.platforms.length > 0 && (
          <section className="editorial-container py-16 border-t border-border">
            <div className="space-y-8">
              <h2 className="text-2xl editorial-serif">Watch Now</h2>
              <div className="flex flex-wrap gap-4">
                {project.platforms.map((platform, idx) => (
                  <a
                    key={idx}
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 border border-border hover:border-primary text-sm uppercase tracking-widest transition-colors"
                    data-testid={`platform-link-${idx}`}
                  >
                    <Play size={14} />
                    {platform.name}
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Achievements */}
        {project.achievements && project.achievements.length > 0 && (
          <section className="editorial-container py-16 border-t border-border">
            <div className="space-y-8">
              <h2 className="text-2xl editorial-serif">Recognition</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {project.achievements.map((achievement, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Award size={16} className="accent-red mt-1 flex-shrink-0" />
                    <span className="text-sm text-foreground/80">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Reviews */}
        {project.reviews && project.reviews.length > 0 && (
          <section className="editorial-container py-16 border-t border-border">
            <div className="space-y-12">
              <h2 className="text-2xl editorial-serif">Reviews</h2>
              <div className="space-y-10">
                {project.reviews.map((review, idx) => (
                  <div key={idx} className="border-l-2 border-primary pl-6">
                    <p className="text-lg editorial-serif leading-relaxed mb-3">
                      "{review.quote}"
                    </p>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">
                      {review.outlet}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Aggregated Content - Press, Updates, Coverage - Collapsible */}
        {projectContent.length > 0 && (
          <section className="editorial-container py-16 border-t border-border">
            <CollapsibleNews 
              title={`Coverage & Updates (${projectContent.length})`}
              items={projectContent}
            />
            <p className="text-sm text-muted-foreground mt-4">
              All coverage, press, and updates related to {project.title}, aggregated from across the ecosystem.
            </p>
          </section>
        )}

        {/* Gallery */}
        {project.gallery && project.gallery.length > 0 && (
          <section className="editorial-container py-16 border-t border-border">
            <div className="space-y-8">
              <h2 className="text-2xl editorial-serif">Gallery</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {project.gallery.map((imageUrl, idx) => (
                  <div key={idx} className="relative aspect-[4/3] bg-black overflow-hidden">
                    <img
                      src={imageUrl}
                      alt={`${project.title} ${idx + 1}`}
                      className="w-full h-full object-cover opacity-90 hover:scale-103 transition-transform duration-700"
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Credits */}
        {project.directors && project.directors.length > 0 && (
          <section className="editorial-container py-16 border-t border-border">
            <div className="space-y-6">
              <h2 className="text-2xl editorial-serif">Directed By</h2>
              <div className="flex flex-wrap gap-6">
                {project.directors.map((director, idx) => (
                  <Link
                    key={idx}
                    to={`/artists`}
                    className="text-sm uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                    data-testid={`director-link-${idx}`}
                  >
                    {director}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* External Link */}
        {project.website && (
          <section className="editorial-container py-16 border-t border-border">
            <div className="text-center">
              <a 
                href={project.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 bg-foreground text-background text-sm uppercase tracking-widest font-medium hover:opacity-90 transition-opacity"
                data-testid="project-website-link"
              >
                Visit Official Site
                <ExternalLink size={16} />
              </a>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default ProjectDetailPage;
